import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCartItem, placeOrder } from '../../redux/cart/cartActions';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as api from '../../api/index';
import {
    Container, Grid, Paper, Title, Text, Button, Group, Stack, Input,
    ActionIcon, Divider, Badge, ScrollArea, Image, LoadingOverlay, TextInput, Textarea,
    createStyles, Loader
} from '@mantine/core';
import { IconTrash, IconMinus, IconPlus, IconShoppingCart, IconTruck } from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
    wrapper: {
        paddingTop: 80,
        paddingBottom: 80,
        minHeight: '100vh',
        backgroundColor: '#050505',
        backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
        overflowX: 'hidden',
        [theme.fn.smallerThan('sm')]: {
            paddingTop: 60,
            paddingBottom: 100, // Extra padding for fixed bottom bar if needed? Or just normal
        },
    },
    glassPanel: {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        padding: theme.spacing.xl,
        borderRadius: theme.radius.lg,
        color: theme.white,
        [theme.fn.smallerThan('sm')]: {
            padding: theme.spacing.md,
        }
    },
    title: {
        color: theme.white,
        fontFamily: `Greycliff CF, ${theme.fontFamily}`,
        marginBottom: theme.spacing.xl,
    },
    itemCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        padding: theme.spacing.md,
        borderRadius: theme.radius.md,
        marginBottom: theme.spacing.sm,
        transition: 'transform 0.2s',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
        }
    },
    input: {
        '& input, & textarea': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: theme.white,
            '&:focus': {
                borderColor: theme.colors.cyan[6],
            }
        },
        label: {
            color: theme.colors.gray[4],
        }
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing.xs,
        color: theme.colors.gray[4],
    },
    summaryTotal: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing.md,
        paddingTop: theme.spacing.md,
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        fontWeight: 700,
        fontSize: 20,
        color: theme.white,
    }
}));

const Cart = () => {
    const { classes, theme } = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const state = useSelector(state => state.cart);

    // Local State
    const [instructions, setInstructions] = useState(state.instructions);
    const [address, setAddress] = useState(state.address);
    const [loading, setLoading] = useState(false);

    // Helpers
    const isAddressValid = () => {
        return address.name && address.phone && address.pin && address.address;
    };

    // Data for Dynamic Calculation
    const calculateCartValues = () => {
        const subTotal = state.cartItems.reduce((acc, item) => {
            return acc + (item.price || item.currentPrice) * item.quantity;
        }, 0);

        const shipping = subTotal > 1500 ? 0 : 90;

        const discount = subTotal * (state.discount || 0) / 100;
        const maxDiscount = state.maxDiscount || 0;
        const appliedDiscount = discount < maxDiscount ? discount : maxDiscount;

        const finalTotal = subTotal + shipping - appliedDiscount;
        const advancePayable = Math.round(finalTotal * 0.20);
        const remainingPayable = finalTotal - advancePayable;

        return { subTotal, shipping, appliedDiscount, finalTotal, advancePayable, remainingPayable };
    };

    const cartValues = calculateCartValues();

    const updateQuantity = (itemId, operator) => {
        const itemIndex = state.cartItems.findIndex(item => item.id === itemId || item._id === itemId);
        if (itemIndex === -1) return;

        let newState = JSON.parse(JSON.stringify(state)); // Use let and defined in outer scope
        const currentQty = newState.cartItems[itemIndex].quantity;

        // Remove logic
        if (currentQty === 1 && operator === -1) {
            newState.cartItems.splice(itemIndex, 1);
            toast.info("Item removed from cart");
        } else {
            newState.cartItems[itemIndex].quantity += operator;
        }

        // recalculateTotals is removed as we use dynamic calculation on render
        // But we still update Redux with basic item changes for persistence
        dispatch(updateCartItem(newState));
    };

    // State for Polling
    const [pollingOrderId, setPollingOrderId] = useState(null);
    const [isPolling, setIsPolling] = useState(false);
    const paymentPopupRef = React.useRef(null);

    // Polling Effect
    useEffect(() => {
        let pollInterval;
        let checkPopupInterval;
        let stopPollingTimeout;

        if (isPolling && pollingOrderId) {
            // 1. Poll Backend Status
            pollInterval = setInterval(async () => {
                try {
                    const { data } = await api.commonFetch(`/orders/${pollingOrderId}`);
                    console.log("Polling Status:", data.status);
                    if (data.status === 'PAID') {
                        setIsPolling(false);
                        clearInterval(pollInterval);
                        clearInterval(checkPopupInterval);
                        if (stopPollingTimeout) clearTimeout(stopPollingTimeout);

                        toast.success("Payment Successful!");
                        // Clear Cart and Redirect
                        dispatch(placeOrder(state, () => {
                            navigate('/thankyou');
                        }));
                    }
                } catch (error) {
                    console.error("Polling Error:", error);
                }
            }, 3000); 

            // 2. Check if Popup Closed
            checkPopupInterval = setInterval(() => {
                if (paymentPopupRef.current && paymentPopupRef.current.closed) {
                    clearInterval(checkPopupInterval);

                    // Allow 10 seconds grace period after close for webhook to process
                    stopPollingTimeout = setTimeout(() => {
                        if (isPolling) {
                            setIsPolling(false);
                            setLoading(false);
                            toast.warning("Payment cancelled or timed out.");
                        }
                    }, 10000);
                }
            }, 1000);
        }
        return () => {
            clearInterval(pollInterval);
            clearInterval(checkPopupInterval);
            if (stopPollingTimeout) clearTimeout(stopPollingTimeout);
        };
    }, [isPolling, pollingOrderId, dispatch, navigate, state]);

    const handleCheckout = async () => {
        if (state.cartItems.length === 0) {
            toast.error("Cart is empty");
            return;
        }
        if (!isAddressValid()) {
            toast.error("Please fill in all shipping details");
            return;
        }

        setLoading(true);
        // Open popup immediately to avoid blocker
        const paymentPopup = window.open('', 'Payment', 'width=600,height=800');
        paymentPopupRef.current = paymentPopup;

        try {
            // Update Redux state with latest address/instructions
            const newState = { ...state, address, instructions };
            dispatch(updateCartItem(newState));

            // Create Payment Event
            const response = await api.createPaymentEvent({
                ...newState,
                userId: 1, // Or fetch actual user ID
                // Override with dynamic values
                total: cartValues.subTotal,
                shipping: cartValues.shipping,
                finalTotal: cartValues.finalTotal,
                advanceAmount: cartValues.advancePayable
            });

            if (response.data && response.data.event_id) {
                const paymentUrl = `https://payments.visibilitylabs.in/pay?event_id=${response.data.event_id}`;

                if (paymentPopup) {
                    paymentPopup.location.href = paymentUrl;
                } else {
                    // Fallback if popup blocked
                    window.location.href = paymentUrl;
                    return;
                }

                setPollingOrderId(response.data.order_id);
                setIsPolling(true); // Start polling
            } else {
                if (paymentPopup) paymentPopup.close();
                toast.error("Failed to initiate payment. Please try again.");
                console.error("Invalid response:", response);
                setLoading(false); // Stop loading if failed
            }

        } catch (error) {
            console.error("Checkout Error:", error);
            if (paymentPopup) paymentPopup.close();
            toast.error("Checkout failed. Please try again later.");
            setLoading(false);
        }
        // Note: We leave loading=true to show the "Processing" overlay while polling
    };

    if (state.cartItems.length === 0) {
        return (
            <div className={classes.wrapper}>
                <Container size="md" style={{ height: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <IconShoppingCart size={80} color={theme.colors.gray[6]} style={{ opacity: 0.5, marginBottom: 20 }} />
                    <Title align="center" style={{ color: theme.white, fontSize: 32 }}>Your Cart is Empty</Title>
                    <Text color="dimmed" align="center" mb="xl">Looks like you haven't added anything yet.</Text>
                    <Link to="/store">
                        <Button size="lg" variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }}>Go to Store</Button>
                    </Link>
                </Container>
            </div>
        );
    }

    return (
        <div className={classes.wrapper}>
            <LoadingOverlay
                visible={loading}
                overlayBlur={2}
                loaderProps={{
                    children: (
                        <Stack align="center" spacing="xs">
                            <Loader variant="dots" size="xl" />
                            <Text color="white" size="lg" weight={500}>
                                {isPolling ? "Waiting for Payment Confirmation..." : "Processing Order..."}
                            </Text>
                            {isPolling && <Text color="dimmed" size="sm">Please complete payment in the popup window</Text>}
                        </Stack>
                    )
                }}
            />
            <Container size="xl">
                <Title className={classes.title}>Shopping Cart</Title>

                <Grid gutter="xl">
                    {/* Left Column: Cart Items */}
                    <Grid.Col md={8}>
                        <Paper className={classes.glassPanel}>
                            <Stack spacing="md">
                                {state.cartItems.map((item) => (
                                    <div key={item.id || item._id} className={classes.itemCard}>
                                        <Group position="apart" sx={(theme) => ({ [theme.fn.smallerThan('xs')]: { flexWrap: 'wrap', gap: theme.spacing.sm } })}>
                                            <Group noWrap sx={(theme) => ({ [theme.fn.smallerThan('xs')]: { width: '100%' } })}>
                                                {/* Image */}
                                                <div style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                                                    <Image
                                                        src={item.isCustom
                                                            ? item.thumbnailUrl
                                                            : (item.coverImage?.startsWith('http') ? item.coverImage : `https://backend.cdp3d.com${item.coverImage}`)}
                                                        width={80} height={80}
                                                        fit="cover"
                                                        withPlaceholder
                                                    />
                                                </div>

                                                {/* Details */}
                                                <div style={{ maxWidth: 300 }}>
                                                    <Text color="white" weight={600} size="lg" style={{ lineHeight: 1.2, marginBottom: 4 }}>
                                                        {item.title}
                                                    </Text>
                                                    {item.isCustom ? (
                                                        <Stack spacing={2}>
                                                            <Badge size="xs" variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }}>Custom 3D Print</Badge>
                                                            <Text size="xs" color="dimmed">Material: {item.parameters.material}</Text>
                                                            <Text size="xs" color="dimmed">Color: {item.parameters.color}</Text>
                                                            <Text size="xs" color="dimmed">Quality: {item.parameters.layerHeight}</Text>
                                                        </Stack>
                                                    ) : (
                                                        <Text size="sm" color="dimmed">Standard Product</Text>
                                                    )}
                                                </div>
                                            </Group>

                                            {/* Price & Quantity */}
                                            <div style={{ textAlign: 'right' }}>
                                                <Text color="cyan" weight={700} size="xl" mb="xs">
                                                    ₹ {(item.price || item.currentPrice)}
                                                </Text>
                                                <Group spacing={8} position="right">
                                                    <ActionIcon
                                                        variant="filled"
                                                        color="dark"
                                                        onClick={() => updateQuantity(item.id || item._id, -1)}
                                                        size="lg"
                                                    >
                                                        {item.quantity === 1 ? <IconTrash size={16} color="red" /> : <IconMinus size={16} />}
                                                    </ActionIcon>
                                                    <Text color="white" weight={600} style={{ width: 25, textAlign: 'center' }}>
                                                        {item.quantity}
                                                    </Text>
                                                    <ActionIcon
                                                        variant="filled"
                                                        color="dark"
                                                        onClick={() => updateQuantity(item.id || item._id, 1)}
                                                        size="lg"
                                                    >
                                                        <IconPlus size={16} />
                                                    </ActionIcon>
                                                </Group>
                                            </div>
                                        </Group>
                                    </div>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid.Col>

                    {/* Right Column: Checkout */}
                    <Grid.Col md={4}>
                        <Stack spacing="lg">
                            {/* Order Summary */}
                            <Paper className={classes.glassPanel}>
                                <Title order={3} color="white" mb="md">Order Summary</Title>

                                <div className={classes.summaryRow}>
                                    <Text>Subtotal</Text>
                                    <Text color="white">₹ {cartValues.subTotal}</Text>
                                </div>
                                {state.discount > 0 && (
                                    <div className={classes.summaryRow}>
                                        <Text>Discount</Text>
                                        <Text color="green">- ₹ {cartValues.appliedDiscount}</Text>
                                    </div>
                                )}
                                <div className={classes.summaryRow}>
                                    <Text>Shipping</Text>
                                    <Text color={cartValues.shipping === 0 ? "green" : "white"}>
                                        {cartValues.shipping === 0 ? "FREE" : `₹ ${cartValues.shipping}`}
                                    </Text>
                                </div>

                                <div className={classes.summaryTotal}>
                                    <Text>Grand Total</Text>
                                    <Text size={24} color="cyan">₹ {cartValues.finalTotal}</Text>
                                </div>

                                {cartValues.subTotal < 1500 && (
                                    <Group position="center" mt="xs">
                                        <IconTruck size={14} color={theme.colors.gray[5]} />
                                        <Text size="xs" color="dimmed">
                                            Add ₹{1500 - cartValues.subTotal} more for Free Shipping
                                        </Text>
                                    </Group>
                                )}

                                <Divider my="md" color="rgba(255,255,255,0.1)" />

                                {/* Partial Payment Section */}
                                <Paper p="sm" style={{ backgroundColor: 'rgba(0, 255, 255, 0.05)', border: '1px solid rgba(0, 255, 255, 0.2)' }} radius="md">
                                    <Text color="cyan" size="sm" weight={600} mb={4} align="center">
                                        Pay 20% Advance to Confirm
                                    </Text>
                                    <Text color="dimmed" size="xs" align="center" mb="md">
                                        The remaining 80% is payable at the time of delivery.
                                    </Text>

                                    <div className={classes.summaryRow}>
                                        <Text color="white" weight={500}>Pay Now (20%)</Text>
                                        <Text color="cyan" weight={700} size="lg">₹ {cartValues.advancePayable}</Text>
                                    </div>
                                    <div className={classes.summaryRow}>
                                        <Text color="dimmed" size="sm">Pay on Delivery (80%)</Text>
                                        <Text color="dimmed" size="sm">₹ {cartValues.remainingPayable}</Text>
                                    </div>
                                </Paper>
                            </Paper>

                            {/* Shipping Details */}
                            <Paper className={classes.glassPanel}>
                                <Title order={4} color="white" mb="md">Shipping Details</Title>
                                <Stack spacing="sm">
                                    <TextInput
                                        placeholder="Full Name"
                                        label="Name"
                                        value={address.name}
                                        onChange={(e) => setAddress({ ...address, name: e.target.value })}
                                        className={classes.input}
                                    />
                                    <TextInput
                                        placeholder="Mobile Number"
                                        label="Phone"
                                        value={address.phone}
                                        onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                                        className={classes.input}
                                    />
                                    <TextInput
                                        placeholder="Email Address"
                                        label="Email"
                                        value={address.email}
                                        onChange={(e) => setAddress({ ...address, email: e.target.value })}
                                        className={classes.input}
                                    />
                                    <Textarea
                                        placeholder="Complete Address"
                                        label="Address"
                                        value={address.address}
                                        onChange={(e) => setAddress({ ...address, address: e.target.value })}
                                        className={classes.input}
                                        minRows={2}
                                    />
                                    <TextInput
                                        placeholder="Pincode"
                                        label="Pincode"
                                        value={address.pin}
                                        onChange={(e) => setAddress({ ...address, pin: e.target.value })}
                                        className={classes.input}
                                    />
                                </Stack>
                            </Paper>

                            <Paper className={classes.glassPanel}>
                                <Title order={4} color="white" mb="md">Instructions</Title>
                                <Textarea
                                    placeholder="Any special requests?"
                                    value={instructions}
                                    onChange={(e) => setInstructions(e.target.value)}
                                    className={classes.input}
                                />
                            </Paper>

                            <Button
                                fullWidth
                                size="xl"
                                variant="gradient"
                                gradient={{ from: 'cyan', to: 'indigo' }} // Swapped for better visibility
                                onClick={handleCheckout}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : `Pay ₹ ${cartValues.advancePayable} & Place Order`}
                            </Button>

                        </Stack>
                    </Grid.Col>
                </Grid>
            </Container>
        </div>
    );
};

export default Cart;